import debounce from 'lodash/debounce'
import * as React from 'react'
import {useLocation} from 'react-router'
import {fromMaybe} from '@freckle/maybe'

import {
  updateDistrictById,
  postDistrict,
  deleteIdentityProviderAssociations
} from '@frontrow/console/src/helpers/api-districts'
import {type FetchDataParamsT} from '@frontrow/console/src/components/common/data-table'
import {parseFilterUrl} from '@frontrow/console/src/helpers/data-table'
import {type BasicDistrictT} from '@frontrow/console/src/models/district'
import {DistrictsDataTable} from '@frontrow/console/src/components/pages/districts-page/districts-data-table'
import {firstResponseApiError} from '@frontrow/console/src/entities/models/api-error'
import {useDistricts} from '@frontrow/console/src/hooks/use-districts'

export function DistrictsPageContainer(): React.ReactElement {
  const location = useLocation()
  const defaultFiltered = parseFilterUrl(location)
  const [fetchDataParams, setFetchDataParams] = React.useState<FetchDataParamsT | null>(null)

  const {data, error, isLoading, mutate} = useDistricts(fetchDataParams)

  const setFetchDataParamsDebounced = debounce((params?: FetchDataParamsT | null) => {
    setFetchDataParams(params ?? null)
  }, 200)

  const handleUpdateDistrict = async (
    districtId: number,
    updatedDistrict: BasicDistrictT,
    clearClassLinkOneRosterToken: boolean
  ): Promise<unknown> => {
    try {
      const response = await updateDistrictById(
        districtId,
        updatedDistrict,
        clearClassLinkOneRosterToken
      )
      await mutate()
      return response
    } catch (err: unknown) {
      const apiError = firstResponseApiError(err)
      throw new Error(apiError.message)
    }
  }

  const handleCreateDistrict = async (districtData: BasicDistrictT): Promise<unknown> => {
    try {
      const response = await postDistrict(districtData)
      await mutate()
      return response
    } catch (err: unknown) {
      const apiError = firstResponseApiError(err)
      throw new Error(apiError.message)
    }
  }

  const handleDeleteCleverAndClasslinkAssociations = async (districtId: number): Promise<void> => {
    try {
      await deleteIdentityProviderAssociations(districtId)
      await mutate()
    } catch (err: unknown) {
      const apiError = firstResponseApiError(err)
      throw new Error(apiError.message)
    }
  }

  const districtsData = fromMaybe(() => [], data)

  return (
    <DistrictsDataTable
      fetchData={setFetchDataParamsDebounced}
      updateDistrict={handleUpdateDistrict}
      createDistrict={handleCreateDistrict}
      districtsData={districtsData}
      isLoading={isLoading}
      error={error?.message ?? null}
      deleteCleverAndClasslinkAssociations={handleDeleteCleverAndClasslinkAssociations}
      defaultFiltered={defaultFiltered}
    />
  )
}
